import { useCallback, useState } from 'react';

import { VSpacing, Loader } from '@hh.ru/magritte-ui';
import ConversionNumber from 'bloko/blocks/conversion';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { formatToReactComponent } from 'bloko/common/trl';

import formatDate from 'Modules/formatDate';
import defaultRequestErrorHandler from 'src/api/notifications/defaultRequestErrorHandler';
import Result from 'src/components/Employer/AssessmentResult';
import { useNotification } from 'src/components/Notifications/Provider';
import Divider from 'src/components/ResumeCard/Divider';
import translation from 'src/components/translation';
import useArrayMemo from 'src/hooks/useArrayMemo';
import { useSelectorNonNullable } from 'src/hooks/useSelector';
import useToggleState from 'src/hooks/useToggleState';
import { Assessment, AssessmentCode, State } from 'src/models/assessments';
import { ResourceType } from 'src/models/negotiationTopic.types';
import fetcher from 'src/utils/fetcher';

import StaticCollapseTrigger from 'src/components/ResumeCard/Body/Collapse/Trigger/Static';
import Field from 'src/components/ResumeCard/Body/Field';

const REMOTE = '/employer/vacancyresponses/assessment_json';

declare global {
    interface FetcherPostApi {
        [REMOTE]: {
            body: {
                ids: number[];
                employerId: number;
            };
            queryParams: void;
            response: {
                assessments: Assessment[];
            };
        };
    }
}

const TrlKeys = {
    title: 'resumeCard.field.assessments',
    [AssessmentCode.SkillazVideoInterview]: 'assessments.tools.SKILLAZ_VIDEO_INTERVIEW',
    result: 'vacancyResponses.assessments.result',
    assessments: {
        one: 'vacancyResponses.assessments.one',
        some: 'vacancyResponses.assessments.some',
        many: 'vacancyResponses.assessments.many',
    },
};

const Assessments: TranslatedComponent<{ topicId: string }> = ({ topicId, trls }) => {
    const { addNotification } = useNotification();

    const assessmentList = useSelectorNonNullable(
        (state) =>
            state.candidatesList.shortTopicList?.topicList[topicId]?.resources.filter(
                (resource) => resource.type === ResourceType.Assessment
            ) ?? []
    );
    const assessmentIdList: number[] = useArrayMemo(assessmentList.map((resource) => resource.id));
    const employerId = useSelectorNonNullable(({ vacanciesShort }) => vacanciesShort.vacanciesList[0]?.company.id);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Assessment[]>([]);
    const [expanded, toggleExpanded, setExpanded] = useToggleState(false);

    const loadAssessments = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetcher.post(REMOTE, { ids: assessmentIdList, employerId });
            setData(response.data.assessments);
        } catch (error) {
            setExpanded(false);
            defaultRequestErrorHandler(error, addNotification);
        } finally {
            setLoading(false);
        }
    }, [addNotification, assessmentIdList, employerId, setExpanded]);

    const revokeFromVacancyResponses = useCallback(({ id }: { id: number }) => {
        setData((assessments) =>
            assessments.map((assessment) =>
                assessment.assessmentId === id
                    ? {
                          ...assessment,
                          revoke: true,
                          state: State.Revoked,
                      }
                    : assessment
            )
        );
    }, []);

    const handleClick = useCallback(() => {
        toggleExpanded();
        if (!expanded && !data.length) {
            void loadAssessments();
        }
    }, [data, expanded, loadAssessments, toggleExpanded]);

    return (
        <Field title={trls[TrlKeys.title]} dataQaName="assessments">
            <StaticCollapseTrigger
                text={formatToReactComponent(trls[TrlKeys.result], {
                    '{0}': (
                        <ConversionNumber
                            value={assessmentIdList.length}
                            one={trls[TrlKeys.assessments.one]}
                            some={trls[TrlKeys.assessments.some]}
                            many={trls[TrlKeys.assessments.many]}
                        />
                    ),
                })}
                expanded={expanded}
                onToggle={handleClick}
            />
            {loading && (
                <>
                    <VSpacing default={8} />
                    <Loader size={16} />
                </>
            )}
            {expanded &&
                data.map((assessment) => (
                    <div key={assessment.assessmentId}>
                        <VSpacing default={8} />
                        {trls[TrlKeys[assessment.code]]}
                        <Divider />
                        <span suppressHydrationWarning>{formatDate(assessment.creationTime, 'dd.MM.yy')}</span>
                        <Divider />
                        <Result
                            assessment={assessment}
                            revokeFromVacancyResponses={revokeFromVacancyResponses}
                            isResumeCard
                        />
                    </div>
                ))}
        </Field>
    );
};

export default translation(Assessments);
